import type { FilterValue, ResolvedFilter } from '../../domain/query/filter';

export type Where = Record<string, unknown>;

function toGraphQLValue(value: FilterValue): unknown {
    return value instanceof Date ? value.toISOString() : value;
}

function dayBounds(value: Date | string | number): [string, string] {
    const d = value instanceof Date ? value : new Date(value);
    const start = new Date(d);
    start.setHours(0, 0, 0, 0);
    const end = new Date(d);
    end.setHours(23, 59, 59, 999);
    return [start.toISOString(), end.toISOString()];
}

function isIdField(field: string): boolean {
    return field === 'id';
}

function writeFilterPredicate(
    field: string,
    filter: ResolvedFilter,
    target: Where,
): void {
    const { operator, type, value, valueTo } = filter;
    if (type === 'date') {
        if (operator === 'equals') {
            const [gte, lte] = dayBounds(value as Date | string | number);
            target[`${field}GTE`] = gte;
            target[`${field}LTE`] = lte;
            return;
        }
        if (operator === 'greaterThanOrEqual' || operator === 'lessThan') {
            const [start] = dayBounds(value as Date | string | number);
            target[
                `${field}${operator === 'greaterThanOrEqual' ? 'GTE' : 'LT'}`
            ] = start;
            return;
        }
        if (operator === 'greaterThan' || operator === 'lessThanOrEqual') {
            const [, end] = dayBounds(value as Date | string | number);
            target[`${field}${operator === 'greaterThan' ? 'GT' : 'LTE'}`] =
                end;
            return;
        }
    }
    switch (operator) {
        case 'contains':
            if (isIdField(field)) target[field] = toGraphQLValue(value);
            else target[`${field}ContainsFold`] = toGraphQLValue(value);
            break;
        case 'equals':
            target[field] = toGraphQLValue(value);
            break;
        case 'greaterThan':
            target[`${field}GT`] = toGraphQLValue(value);
            break;
        case 'greaterThanOrEqual':
            target[`${field}GTE`] = toGraphQLValue(value);
            break;
        case 'lessThan':
            target[`${field}LT`] = toGraphQLValue(value);
            break;
        case 'lessThanOrEqual':
            target[`${field}LTE`] = toGraphQLValue(value);
            break;
        case 'oneOf':
            target[`${field}In`] = toGraphQLValue(value);
            break;
        case 'between':
            target[`${field}GTE`] = toGraphQLValue(value);
            if (valueTo !== undefined)
                target[`${field}LTE`] = toGraphQLValue(valueTo);
            break;
    }
}

function isEdgeSegment(segment: string): boolean {
    return segment.startsWith('has') && segment.endsWith('With');
}

function writeNestedPredicate(
    root: Where,
    key: string,
    filter: ResolvedFilter,
): void {
    const segments = key.split('.');
    let cursor = root;
    for (let i = 0; i < segments.length - 1; i++) {
        const seg = segments[i];
        if (isEdgeSegment(seg)) {
            const existing = cursor[seg];
            const list = Array.isArray(existing) ? (existing as Where[]) : [{}];
            cursor[seg] = list;
            cursor = list[0];
        } else {
            if (typeof cursor[seg] !== 'object' || cursor[seg] === null) {
                cursor[seg] = {};
            }
            cursor = cursor[seg] as Where;
        }
    }
    writeFilterPredicate(segments[segments.length - 1], filter, cursor);
}

export function searchWhere(text: string, keys: string[]): Where | undefined {
    if (!text || keys.length === 0) return undefined;
    return {
        or: keys.map((key) => {
            const clause: Where = {};
            writeNestedPredicate(clause, key, {
                key,
                type: 'text',
                operator: 'contains',
                value: text,
            });
            return clause;
        }),
    };
}

export function filterWhere(filters: ResolvedFilter[]): Where {
    const where: Where = {};
    for (const filter of filters)
        writeNestedPredicate(where, filter.key, filter);
    return where;
}

export interface BuildWhereArgs {
    base?: Where;
    filters: ResolvedFilter[];
    search: string;
    searchFieldPaths: string[];
}

export function buildWhere(args: BuildWhereArgs): Where | undefined {
    const { base, filters, search, searchFieldPaths } = args;
    const parts: Where[] = [];
    if (base) parts.push(base);
    if (filters.length > 0) parts.push(filterWhere(filters));
    const searchPart = searchWhere(search, searchFieldPaths);
    if (searchPart) parts.push(searchPart);

    if (parts.length === 0) return undefined;
    if (parts.length === 1) return parts[0];
    return { and: parts };
}
